import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  MobileOutlined,
  SettingOutlined,
  UserOutlined,
} from '@ant-design/icons';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import UserAssignmentSelector from './selectors/UserAssignmentSelector';
import WorkflowPushNotificationConfigureDrawer from './WorkflowPushNotificationConfigureDrawer';
import NuxPopover from '../../nux/NuxPopover';

import {
  FORMS_WORKFLOW_STEP_4,
  FORMS_WORKFLOW_STEP_TARGET_TEXT,
} from '../../nux/nux.constants';
import config from '../../config';
import { getIdMap, mergeSets } from '../../helpers/helpers';
import { resolveFormDivisionIds } from '../formHelpers';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import { Row } from 'antd';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Select Recipient(s)</b> field to be configured.
    </div>
  ),
};

export default function WorkflowNotificationNode({
  type,
  id,
  draggable,
  onNodeUpdate,
  setDataMap,
  isDisplay,
  hover,
  divisionId,
  divisionIds,
  data = {},
  style = {},
  additionalOptions = [],
  children,
  showFormAuthor,
  formAuthorHelperText,
  sections = [],
  isExternalForm,
  setDisableNext,
}) {
  const {
    users: initialSelected,
  } = data;
  const [selected, setSelected] = useState(initialSelected || []);
  const [showDrawer, setShowDrawer] = useState(false);

  useEffect(() => {
    if (!setDisableNext) return;

    setDisableNext((prev) => ({ ...prev, [id]: selected?.length === 0 }));
  }, [selected, setDisableNext, id]);

  const teams = useSelector((state) => state.teams.teams);
  const divisions = useSelector(state => state.settings.divisions);
  const activeNuxAction = useSelector(state => state.nux.activeNuxAction);
  const { userIds } = useSelector(state => {
    const {
      users: {
        users = [],
      } = {},
    } = state;
    const idSet = new Set();
    users.forEach((user) => {
      if(user.active) {
        idSet.add(user.id);
      }
    });
    return { userIds: idSet };
  });

  const teamsMap = useMemo(() => getIdMap(teams), [teams]);
  const relevantDivisionIds = useMemo(
    () => resolveFormDivisionIds({ divisionIds, divisionId }),
    [divisionIds, divisionId],
  );
  const additionalIds = useMemo(() => new Set(additionalOptions.map((addOpt) => addOpt.id)),[additionalOptions]);
  const {
    title,
    Icon,
    tokenSeparators,
    mode = 'multiple',
    text = 'Select Recipient(s):',
    validate,
  } = useMemo(() => {
    if (type === 'users') {
      return {
        title:'Assigned Users',
        Icon: UserOutlined,
        tokenSeparators:null,
      }
    } else {
      return {
        title:'Notify in App',
        Icon: MobileOutlined,
        tokenSeparators:null,
      };
    }
  },[type,userIds,additionalIds]);

  const onOpenDrawer = useCallback(() => setShowDrawer(true), []);
  const onCloseDrawer = useCallback(() => setShowDrawer(false), []);
  const onChange = useCallback((newSelected) => {
    let filteredSelected = newSelected;
    if(validate) {
      filteredSelected = validate(newSelected);
    }

    setSelected(filteredSelected);

    if(setDataMap) {
      setDataMap((dataMap) => {
        const {
          [id]: oldData = {},
        } = dataMap;
        return {
          ...dataMap,
          [id]: {
            ...oldData,
            ['users']: filteredSelected,
          },
        };
      })
    }
  },[validate,id,setDataMap]);

  useEffect(() => {
    if (!initialSelected?.length || !relevantDivisionIds.length) return;

    let ourSelected = initialSelected;
    if (config.showDivisions) {
      const divisionUserIds = mergeSets(
        relevantDivisionIds.map((divId) => divisions[divId]?.users ?? new Set()),
      );
      ourSelected = initialSelected.filter(
        (userId) => userId.length !== 36 || divisionUserIds.has(userId) || userId in teamsMap,
      );
    }
    setSelected(ourSelected);
  }, [initialSelected, relevantDivisionIds, divisions, teamsMap]);

  const isNuxStep4 = activeNuxAction === FORMS_WORKFLOW_STEP_4;

  return (
    <WorkflowActionNode
      title={title}
      Icon={Icon}
      type={type}
      id={id}
      draggable={draggable}
      onNodeUpdate={onNodeUpdate}
      style={style}
      isDisplay={isDisplay}
      hover={hover}
      errorPopover={selected?.length === 0 && setDisableNext ? ERROR_DETAILS : undefined}
    >
      {!draggable && <div>
        {children}
        <UserAssignmentSelector
          isDisplay={isDisplay}
          draggable={draggable}
          divisions={relevantDivisionIds}
          onChange={onChange}
          selected={selected}
          mode={mode}
          showPositions={type === 'users'}
          type={type}
          text={text}
          tokenSeparators={tokenSeparators}
          additionalOptions={additionalOptions}
          showTeams
          showFormAuthor={showFormAuthor && !isExternalForm}
          formAuthorHelperText={formAuthorHelperText}
          isExternalForm={isExternalForm}
          testId="assignment"
        />
        <NuxPopover
          placement='rightTop'
          visible={isNuxStep4}
          text={FORMS_WORKFLOW_STEP_TARGET_TEXT}
        >
          <WorkflowHandle type='target' position='top' disabled={isDisplay}/>
        </NuxPopover>
        {type === 'users' &&
          <WorkflowHandle type='source' position='bottom' disabled/>
        }
        {type === 'push' &&
          <div>
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title='Configure'
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={onOpenDrawer}
              />
            </Row>
            <WorkflowPushNotificationConfigureDrawer
              id={id}
              visible={showDrawer}
              onClose={onCloseDrawer}
              onSubmit={onCloseDrawer}
              data={data}
              setDataMap={setDataMap}
              sections={sections}
            />
          </div>
        }
      </div>}
    </WorkflowActionNode>
  );
}
